import React from 'react';
import {
  Grid,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  Box,
  Link,
} from '@material-ui/core';
import colors from '../../../../themes/main-colors';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';
import { useTranslation } from '../../../../hooks/useTranslation';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useOutageEventsDetails } from '../../hooks/useOutageEventsDetails';
import Backdrop from '../../../backdrop';
import {
  ShowByType,
  useOutageMapOptions,
} from '../../providers/OutageMapOptionsProvider';
import { LastUpdatedSection } from '../last-updated-section';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    expandedContainer: {
      backgroundColor: colors.riverBlue,
      padding: theme.spacing(2.5),
      flexDirection: 'column',
      gap: theme.spacing(2),
      '& p': {
        margin: 0,
      },
    },
    card: {
      backgroundColor: colors.white,
      padding: theme.spacing(1.25, 2),
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: '5rem',
      maxHeight: '5rem',
    },
    value: {
      fontSize: '1.125rem',
      fontWeight: 600,
    },
    label: {
      fontSize: '1rem',
      lineHeight: '100%',
    },
    collapsedContainer: {
      flexDirection: 'column',
      padding: theme.spacing(2.5, 1.5),
      textAlign: 'center',
    },
    collapsedValue: {
      fontSize: '0.875rem',
      fontWeight: 600,
    },
    collapsedLabel: {
      fontSize: '0.75rem',
      lineHeight: '100%',
    },
    link: {
      cursor: 'pointer',
      textDecoration: 'underline',
    },
  }),
);

export function OutageSummary() {
  const classes = useStyles();
  const { panelOpen: expanded } = useOutageLeftPanelState();
  const { t, richT } = useTranslation();
  const {
    activeOutages,
    customersAffected,
    customersWithPower,
    totalPlannedOutages,
    loading,
  } = useOutageEventsDetails();
  const { outageToggles, setOutageToggles, setShowBy } = useOutageMapOptions();
  const spinnerText = t('LOADING_OUTAGE_INFO_SPINNER');
  const { setPage } = useOutageResponsivePageTracker();

  return (
    <>
      {expanded ? (
        <Grid
          container
          direction="column"
          className={classes.expandedContainer}
        >
          {/* Cards Grid */}
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Box className={classes.card}>
                <Typography className={classes.value}>
                  {customersWithPower}
                </Typography>
                <Typography className={classes.label}>
                  {t('TOTAL_PERCENT_CUSTOMERS_AFFECTED')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className={classes.card}>
                <Typography className={classes.value}>
                  {customersAffected.toLocaleString()}
                </Typography>
                <Typography className={classes.label}>
                  {t('CUSTOMERS_AFFECTED')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className={classes.card}>
                <Typography className={classes.value}>
                  {activeOutages.toLocaleString()}
                </Typography>
                <Link
                  className={clsx(classes.label, classes.link)}
                  onClick={() => {
                    setShowBy(ShowByType.DEFAULT);
                    setOutageToggles({
                      planned: true,
                      unplanned: true,
                      psps: false,
                    });
                    setPage(ResponsivePageView.Map);
                  }}
                >
                  {t('ACTIVE_OUTAGES')}
                </Link>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className={classes.card}>
                <Typography className={classes.value}>
                  {totalPlannedOutages}
                </Typography>
                <Link
                  className={clsx(classes.label, classes.link)}
                  onClick={() => {
                    setShowBy(ShowByType.DEFAULT);
                    setOutageToggles({
                      planned: true,
                      unplanned: false,
                      psps: false,
                    });
                    setPage(ResponsivePageView.Map);
                  }}
                >
                  {t('PLANNED_OUTAGES')}
                </Link>
              </Box>
            </Grid>
          </Grid>

          <Box mt={2.5}>
            <LastUpdatedSection />
          </Box>
        </Grid>
      ) : (
        <Grid
          container
          direction="column"
          className={classes.collapsedContainer}
        >
          <Typography className={classes.collapsedValue}>
            {customersWithPower}
          </Typography>
          <Typography className={classes.collapsedLabel}>
            {t('WITH_POWER')}
          </Typography>
        </Grid>
      )}
    </>
  );
}
